/**
 * Full-text search over transcript passages, shared by the build step (which
 * serialises the index) and the client (which loads and queries it).
 *
 * Grain and weight. Lines are grouped into short passages so the index stays
 * light on a phone, and English and the original are indexed as two separate
 * shard families: the client always loads the English family, and only loads
 * the (equally large) original-language family when the query itself contains
 * Devanagari. Text is never stored in the index — a hit carries only the video
 * and the passage's start time; the exact matched line is resolved from the
 * transcript, which the video view loads anyway.
 */
import MiniSearch from "minisearch";
import type { Options, SearchResult } from "minisearch";

import { asNumber, asString } from "./decode";
import type {
  SearchDoc,
  SearchHit,
  TranscriptSegment,
  VideoDoc,
} from "./types";

/** Lines per passage. */
const PASSAGE_LINES = 5;
/** Field sets for the two index families. */
export const ENGLISH_FIELDS: readonly string[] = ["en"];
export const ORIGINAL_FIELDS: readonly string[] = ["o"];
const STORE_FIELDS: readonly string[] = ["v", "t"];

/**
 * Split on whitespace, punctuation and symbols — the Unicode classes cover the
 * Devanagari danda (।) and double danda, so "होती।" tokenises to "होती".
 */
const TOKEN_SPLIT = /[\s\p{P}\p{S}]+/u;

export function tokenize(text: string): string[] {
  return text.split(TOKEN_SPLIT).filter((token) => token.length > 0);
}

/** Whether a query carries Devanagari, and so needs the original-text index. */
export function hasDevanagari(text: string): boolean {
  return /[ऀ-ॿ]/u.test(text);
}

/** MiniSearch options for a family; `fields` decides what that family indexes. */
function searchOptions(fields: readonly string[]): Options<SearchDoc> {
  return {
    idField: "id",
    fields: [...fields],
    storeFields: [...STORE_FIELDS],
    tokenize,
    processTerm: (term) => term.toLowerCase(),
  };
}

/** Group a video's lines into passage documents (one per index family). */
export function buildPassageDocs(video: VideoDoc): SearchDoc[] {
  const docs: SearchDoc[] = [];
  for (let start = 0; start < video.segments.length; start += PASSAGE_LINES) {
    const lines = video.segments.slice(start, start + PASSAGE_LINES);
    const en = lines
      .map((line) => line.en)
      .join(" ")
      .trim();
    const o = lines
      .map((line) => line.orig)
      .join(" ")
      .trim();
    if (en.length === 0 && o.length === 0) {
      continue;
    }
    docs.push({
      id: `${video.id}#${start}`,
      v: video.id,
      t: lines[0].t,
      en,
      o,
    });
  }
  return docs;
}

/** A fresh, empty index for one family. */
export function createIndex(fields: readonly string[]): MiniSearch<SearchDoc> {
  return new MiniSearch<SearchDoc>(searchOptions(fields));
}

/** Restore a serialised shard of a family for querying. */
export function loadShard(
  json: string,
  fields: readonly string[],
): MiniSearch<SearchDoc> {
  return MiniSearch.loadJSON<SearchDoc>(json, searchOptions(fields));
}

/** Stable shard index for a video id, so its passages always co-locate. */
export function shardFor(videoId: string, shardCount: number): number {
  let hash = 0;
  for (let i = 0; i < videoId.length; i += 1) {
    hash = (hash * 31 + videoId.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % shardCount;
}

/** Strip the surrounding quotes of a phrase query, or `null` if it is not one. */
export function parsePhrase(query: string): string | null {
  const trimmed = query.trim();
  if (trimmed.length >= 2 && trimmed.startsWith('"') && trimmed.endsWith('"')) {
    const inner = trimmed.slice(1, -1).trim();
    return inner.length > 0 ? inner : null;
  }
  return null;
}

/** The terms MiniSearch should match: a phrase's inner text, else the query. */
function queryTerms(query: string): string {
  return parsePhrase(query) ?? query.trim();
}

/**
 * Query every loaded family and merge hits by score. Hits are passage anchors;
 * the caller resolves the exact line from the transcript (and applies any
 * phrase filter there, where the line text is available).
 */
export function runQuery(
  indexes: readonly MiniSearch<SearchDoc>[],
  query: string,
  limit: number,
): SearchHit[] {
  const terms = queryTerms(query);
  if (terms.length === 0) {
    return [];
  }
  const results: SearchResult[] = [];
  for (const index of indexes) {
    const found = index.search(terms, { prefix: true, combineWith: "AND" });
    for (const result of found) {
      results.push(result);
    }
  }
  results.sort((a, b) => b.score - a.score);

  const seen = new Set<string>();
  const hits: SearchHit[] = [];
  for (const result of results) {
    const videoId = asString(result.v, "");
    const t = asNumber(result.t, 0);
    const key = `${videoId}#${t}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    hits.push({ videoId, t, score: result.score });
    if (hits.length >= limit) {
      break;
    }
  }
  return hits;
}

/** The passage (up to PASSAGE_LINES lines) that begins at time `t`. */
export function resolvePassage(
  segments: readonly TranscriptSegment[],
  t: number,
): TranscriptSegment[] {
  const start = segments.findIndex((segment) => segment.t === t);
  if (start === -1) {
    return [];
  }
  return segments.slice(start, start + PASSAGE_LINES);
}

/**
 * The line within a passage that a query actually matched, for display; the
 * first line otherwise. Devanagari terms are checked against the original text.
 */
export function matchedLine(
  passage: readonly TranscriptSegment[],
  query: string,
): TranscriptSegment | null {
  if (passage.length === 0) {
    return null;
  }
  const terms = tokenize(queryTerms(query).toLowerCase());
  for (const segment of passage) {
    const en = segment.en.toLowerCase();
    const orig = segment.orig.toLowerCase();
    if (terms.some((term) => en.includes(term) || orig.includes(term))) {
      return segment;
    }
  }
  return passage[0];
}
